'use client'

import { useMemo, useState } from 'react'
import {
  EntityListShell,
  PageHeader,
  Pagination,
  SearchInput,
  Spinner,
  StatusChip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@rr/ui'
import { useCarriersList } from '@/modules/carriers/hooks/useCarriers'

const PAGE_SIZE = 10

export default function CarriersPage() {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const filters = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      search: search.trim() || undefined,
    }),
    [page, search]
  )

  const { data, isLoading, isError, error } = useCarriersList(filters as any)
  const items: any[] = (data as any)?.items ?? (data as any)?.data?.items ?? []
  const total: number =
    (data as any)?.pagination?.total ??
    (data as any)?.data?.pagination?.total ??
    items.length

  return (
    <div className="page-pad">
      <div className="max-w-7xl mx-auto space-y-6">
        <PageHeader
          title="Carriers"
          subtitle="Carrier partners across every tenant fleet."
        />

        <EntityListShell
          toolbar={
            <SearchInput
              value={search}
              onChange={(v) => {
                setSearch(v)
                setPage(1)
              }}
              placeholder="Search carriers…"
              className="max-w-sm"
            />
          }
          empty={!isLoading && items.length === 0}
          emptyState={
            isError
              ? error instanceof Error
                ? error.message
                : 'Failed to load carriers.'
              : 'No carriers match your filters.'
          }
          pagination={
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onChange={setPage}
            />
          }
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Spinner />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Vehicles</TableHead>
                  <TableHead>Drivers</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row: any) => {
                  const id = String(row.id ?? '')
                  const name = row.name ?? row.legalName ?? row.companyName ?? '—'
                  const status = String(row.status ?? 'active')
                  const country =
                    row.country ?? row.countryCode ?? row.country?.code ?? '—'
                  const vehicles =
                    row.vehiclesCount ?? row.vehicles ?? row.vehicleCount ?? '—'
                  const drivers =
                    row.driversCount ?? row.drivers ?? row.driverCount ?? '—'
                  return (
                    <TableRow key={id || name}>
                      <TableCell className="font-medium text-foreground">
                        {name}
                      </TableCell>
                      <TableCell>
                        <StatusChip status={status} />
                      </TableCell>
                      <TableCell className="text-sm">{country}</TableCell>
                      <TableCell className="tnum text-sm">{vehicles}</TableCell>
                      <TableCell className="tnum text-sm">{drivers}</TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </EntityListShell>
      </div>
    </div>
  )
}
